import { useState } from "react";
import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts";
import { Icon } from "@circle-react/components/shared/Icon";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";
import { ConversationFilters } from "../components/ConversationFilters";
import { ConversationItem } from "../components/common/ConversationItem";
import type { Conversation } from "../components/common/ConversationItem";
import { ListViewSkeletonLoader } from "../components/common/ListViewSkeletonLoader";
import { useListConversations } from "../hooks/useListConversations";
import type { ConversationChannelFilterOptions } from "../types/ConversationChannelFilterOptions";
import type { ConversationStatusFilterOptions } from "../types/ConversationStatusFilterOptions";

export const ConversationsListPage = () => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<ConversationChannelFilterOptions>("all");
  const [status, setStatus] = useState<ConversationStatusFilterOptions>("all");
  const { currentCommunityMember } = usePunditUserContext();

  const { isLoading, data } = useListConversations({ type, status });

  const conversations = data?.conversations || [];

  const setFilters = (filters: {
    type: ConversationChannelFilterOptions;
    status: ConversationStatusFilterOptions;
  }) => {
    setType(filters.type);
    setStatus(filters.status);
  };

  useWebSocket(
    {
      channel: "HelpWidget::GeneralChannel",
      onMessageReceive: (data: { event: string }) => {
        if (data.event === "reloadConversations") {
          void queryClient.invalidateQueries({
            queryKey: ["help-widget", "conversations"],
            exact: false,
          });
        }
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: Boolean(currentCommunityMember?.id),
    },
    [currentCommunityMember?.id],
  );

  return (
    <div className="scrollbar-hide flex size-full flex-col overflow-auto">
      <Form
        preventAccidentalLeave
        id="conversations-filter-form"
        mode="onChange"
        onSubmit={() => {}}
        defaultValues={{
          type: "all",
          status: "all",
        }}
      >
        <ConversationFilters onFiltersChange={setFilters} />
      </Form>

      {isLoading && <ListViewSkeletonLoader />}

      {conversations.length === 0 && !isLoading && (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-1 text-center text-sm">
            <Icon type="sw-conversations" size={20} />
            <Typography.BodySm weight="font-medium" color="text-primary">
              {t("help_widget.conversation_list_page.no_conversations")}
            </Typography.BodySm>
          </div>
        </div>
      )}

      <div className="flex flex-col">
        {conversations.map((conversation: Conversation, index: number) => (
          <div key={conversation.id}>
            <ConversationItem conversation={conversation} />
            {index < conversations.length - 1 && (
              <div className="bg-secondary-dark h-px max-h-[1px]" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
